import { type PaperColor, paperColors } from '@aurea/contracts';
import { Button, Chip, clsx, Dialog, Field, Input, Select } from '@aurea/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { Plus, User } from 'lucide-react';
import { type FormEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { GroupChip, GroupDialog, GroupFormDialog } from '../../components/GroupDialogs';
import { api } from '../../lib/api';
import { useErrorText } from '../../lib/i18n';
import { canManage, useMe } from '../_app';

export const Route = createFileRoute('/_app/courses')({ component: Courses });

function Courses() {
  const me = useMe();
  const { t } = useTranslation();
  const disciplines = useQuery({ queryKey: ['disciplines'], queryFn: api.disciplines });
  const courses = useQuery({ queryKey: ['courses'], queryFn: api.courses });
  const groups = useQuery({ queryKey: ['groups'], queryFn: api.groups });
  const [dialog, setDialog] = useState<
    'discipline' | 'course' | { group: string } | { newGroup: string } | null
  >(null);

  return (
    <>
      <header className="page-head">
        <h1>{t('courses.title')}</h1>
        <div className="flex-1" />
        {canManage(me) && (
          <div className="flex gap-2">
            <Button variant="ghost" onClick={() => setDialog('discipline')}>
              <Plus className="i" />
              {t('courses.addDiscipline')}
            </Button>
            <Button onClick={() => setDialog('course')} disabled={!disciplines.data?.length}>
              <Plus className="i" />
              {t('courses.addCourse')}
            </Button>
          </div>
        )}
      </header>

      {courses.data?.length === 0 && <p className="tray tray-pad text-sm text-ink-2">{t('courses.empty')}</p>}
      <div className="flex flex-col gap-8">
        {disciplines.data?.map((d) => {
          const list = courses.data?.filter((c) => c.disciplineId === d.id) ?? [];
          return (
            <section key={d.id} aria-labelledby={`d-${d.id}`}>
              <h2 id={`d-${d.id}`} className="mb-3 flex items-center gap-2 text-xl font-semibold">
                <Chip color={d.color} />
                {d.name}
              </h2>
              <div className="grid grid-cols-[repeat(auto-fill,minmax(240px,1fr))] gap-4">
                {list.map((c) => (
                  <article
                    key={c.id}
                    className="paper sticker flex min-h-36 flex-col gap-1 p-4"
                    data-c={c.color}
                  >
                    <h3 className="font-semibold leading-tight">{c.name}</h3>
                    <p className="meta">
                      {t(`courses.${c.format}`)} · {t('courses.minutes', { count: c.defaultDurationMin })}
                    </p>
                    <p className="meta">{t('plural.students', { count: c.students })}</p>
                    {c.format !== 'individual' && (
                      <div className="mt-2 flex flex-wrap gap-1.5">
                        {groups.data
                          ?.filter((g) => g.courseId === c.id)
                          .map((g) => (
                            <GroupChip key={g.id} g={g} onOpen={() => setDialog({ group: g.id })} />
                          ))}
                        {canManage(me) && (
                          <button
                            type="button"
                            className="group-chip group-chip-add"
                            onClick={() => setDialog({ newGroup: c.id })}
                          >
                            <Plus className="i size-3.5" aria-hidden="true" />
                            {t('groups.new')}
                          </button>
                        )}
                      </div>
                    )}
                    {c.teachers.length > 0 && (
                      <p className="meta mt-auto pt-2">
                        <User className="i" aria-hidden="true" />
                        {c.teachers.map((x) => x.name).join(', ')}
                      </p>
                    )}
                  </article>
                ))}
              </div>
            </section>
          );
        })}
      </div>
      {dialog === 'discipline' && <DisciplineDialog onClose={() => setDialog(null)} />}
      {dialog === 'course' && <CourseDialog onClose={() => setDialog(null)} />}
      {dialog && typeof dialog === 'object' && 'group' in dialog && (
        <GroupDialog id={dialog.group} admin={canManage(me)} onClose={() => setDialog(null)} />
      )}
      {dialog && typeof dialog === 'object' && 'newGroup' in dialog && (
        <GroupFormDialog courseId={dialog.newGroup} onClose={() => setDialog(null)} />
      )}
    </>
  );
}

function DisciplineDialog({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation();
  const errorText = useErrorText();
  const qc = useQueryClient();
  const [color, setColor] = useState<PaperColor>('sky');
  const create = useMutation({
    mutationFn: api.createDiscipline,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['disciplines'] });
      onClose();
    },
  });
  const submit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    create.mutate({ name: String(new FormData(e.currentTarget).get('name')).trim(), color });
  };
  return (
    <Dialog open onClose={onClose} title={t('courses.addDiscipline')} strip={color}>
      <form className="flex flex-col gap-4 pb-6" onSubmit={submit}>
        <Field label={t('courses.disciplineName')}>
          <Input name="name" required autoFocus />
        </Field>
        <fieldset>
          <legend className="mb-2 text-sm font-medium text-ink-2">{t('courses.color')}</legend>
          <div className="flex flex-wrap gap-2">
            {paperColors.map((c) => (
              <button
                key={c}
                type="button"
                className={clsx(
                  'paper sticker size-10 transition-transform',
                  color === c && '-translate-y-1 ring-2 ring-ink ring-offset-2 ring-offset-tray',
                )}
                data-c={c}
                aria-pressed={color === c}
                aria-label={t(`colors.${c}`)}
                title={t(`colors.${c}`)}
                onClick={() => setColor(c)}
              />
            ))}
          </div>
        </fieldset>
        {create.isError && <p className="text-sm text-danger">{errorText(create.error)}</p>}
        <div className="flex gap-3">
          <Button type="submit" loading={create.isPending}>
            {t('common.create')}
          </Button>
          <Button variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}

function CourseDialog({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation();
  const errorText = useErrorText();
  const qc = useQueryClient();
  const disciplines = useQuery({ queryKey: ['disciplines'], queryFn: api.disciplines });
  const teachers = useQuery({ queryKey: ['teachers'], queryFn: api.teachers });
  const [picked, setPicked] = useState<string[]>([]);
  const create = useMutation({
    mutationFn: api.createCourse,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['courses'] });
      qc.invalidateQueries({ queryKey: ['disciplines'] });
      onClose();
    },
  });
  const submit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const f = new FormData(e.currentTarget);
    create.mutate({
      disciplineId: String(f.get('disciplineId')),
      name: String(f.get('name')).trim(),
      format: String(f.get('format')),
      defaultDurationMin: Number(f.get('duration')),
      teacherIds: picked,
    });
  };
  return (
    <Dialog open onClose={onClose} title={t('courses.addCourse')} strip="lilac">
      <form className="flex flex-col gap-4 pb-6" onSubmit={submit}>
        <Field label={t('courses.discipline')}>
          <Select name="disciplineId" required>
            {disciplines.data?.map((d) => (
              <option key={d.id} value={d.id}>
                {d.name}
              </option>
            ))}
          </Select>
        </Field>
        <Field label={t('courses.courseName')}>
          <Input name="name" required autoFocus />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label={t('courses.format')}>
            <Select name="format" defaultValue="group">
              <option value="group">{t('courses.group')}</option>
              <option value="individual">{t('courses.individual')}</option>
              <option value="mixed">{t('courses.mixed')}</option>
            </Select>
          </Field>
          <Field label={t('courses.duration')}>
            <Input name="duration" type="number" min={15} max={480} step={5} defaultValue={60} required />
          </Field>
        </div>
        <fieldset>
          <legend className="mb-2 text-sm font-medium text-ink-2">{t('courses.teachers')}</legend>
          <div className="flex max-h-40 flex-col gap-1 overflow-y-auto">
            {teachers.data?.map((x) => (
              <label key={x.id} className="check">
                <input
                  type="checkbox"
                  checked={picked.includes(x.id)}
                  onChange={(e) =>
                    setPicked((p) => (e.target.checked ? [...p, x.id] : p.filter((i) => i !== x.id)))
                  }
                />
                {x.firstName} {x.lastName}
              </label>
            ))}
          </div>
        </fieldset>
        {create.isError && <p className="text-sm text-danger">{errorText(create.error)}</p>}
        <div className="flex gap-3">
          <Button type="submit" loading={create.isPending}>
            {t('common.create')}
          </Button>
          <Button variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
